import { createReducer, on } from '@ngrx/store';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { extractAttachments } from '@src/suite/common/utils/mos-utlis';
import { updateReadOnlyCriteria, updateVisibility } from '@src/suite/common/utils/record-utils';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';

import { OPTION_BLANK } from '../../enums/constants';
import { toLuxonLocaleDateString } from '../../utils/date-utils';
import { isStoryTextEqual } from '../../utils/story-text-compare.utils';
import { extractNoteBeginText } from '../../utils/util';
import { FullscreenActions, OpenFullscreenModalActions, SidecarEfffectActions } from '../fullscreen';
import {
    AssignmentRecordActions,
    DiscoverRecordActions,
    NotesRecordActions,
    PublicationRecordActions,
    RecordHttpActions,
    RundownRecordActions,
    StoriesRecordActions,
    TopicRecordActions,
    UserRecordActions,
} from './records.actions';
import { AssignmentsRecordState, DiscoverRecordState, NotesRecordState, PublicationsRecordState, RundownsRecordState, StoriesRecordState, TopicsRecordState, UsersRecordState } from './records.models';

export const initialTopicState: TopicsRecordState = {
    topics: new Map<string, StorytellingTopicRecord>(),
    topicTemplates: new Map<string, StorytellingTopicRecord>(),
    attachedTopicGuids: new Set<string>(),
    currentViewTopics: [],
    storedViewStates: null,
    currentSelectedViewState: null,
};

export const initialAssignmentsState: AssignmentsRecordState = {
    assignments: new Map(),
};

export const initialStoriesState: StoriesRecordState = {
    stories: new Map(),
    storyTemplates: new Map(),
};

export const initialStorytellingUsersState: UsersRecordState = {
    users: new Map<string, StorytellingUserRecord>(),
};

export const initialRundownsState: RundownsRecordState = {
    rundowns: new Map<string, StorytellingRundownRecord>(),
    rundownTemplates: new Map<string, StorytellingRundownRecord>(),
};

export const initialPublicationsState: PublicationsRecordState = {
    publicationChannels: new Map<string, StorytellingPublicationChannelRecord>(),
    publications: new Map<string, StorytellingPublicationRecord>(),
    publicationList: new Map<string, StorytellingPublicationListRecord>(),
};

export const initialDiscoverRecordState: DiscoverRecordState = {
    discoverItems: new Map<string, StorytellingRecord>(),
};

export const initialNotesState: NotesRecordState = {
    notes: new Map<string, StorytellingNoteRecord>(),
    notesExpandState: {},
    noteTemplates: new Map<string, StorytellingNoteRecord>(),
};

export const topicRecordsReducer = createReducer(
    initialTopicState,
    on(TopicRecordActions.setTopics, (state, action): TopicsRecordState => {
        const records = action.records.values().reduce((acc, topic) => {
            const vis = updateVisibility(topic);
            const readOnly = updateReadOnlyCriteria(topic);
            acc.set(topic.Guid, { ...topic, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingTopicRecord>());
        return {
            ...state,
            topics: records,
        };
    }),
    on(TopicRecordActions.setAttachedTopicGuids, (state, action): TopicsRecordState => {
        const attachedTopicGuids = new Set(action.guids);
        return {
            ...state,
            attachedTopicGuids,
        };
    }),
    on(TopicRecordActions.loadTopicViewStatesSuccess, (state, { data }): TopicsRecordState => {
        return {
            ...state,
            storedViewStates: data,
        };
    }),
    on(TopicRecordActions.setCurrentTopicViewState, (state, { viewState }): TopicsRecordState => {
        return {
            ...state,
            currentSelectedViewState: state?.storedViewStates?.find(view => view.Guid === viewState.Guid) || null,
        };
    }),
    on(TopicRecordActions.loadTopicsSuccess, (state, { topics }): TopicsRecordState => {
        return {
            ...state,
            currentViewTopics: Array.from(topics.values()),
        };
    }),
    on(TopicRecordActions.updateTopics, (state, { records }): TopicsRecordState => {
        if (!records?.length) {
            return state;
        }

        const topicsMap = records.reduce((acc, topic) => {
            const existingTopic = state.topics.get(topic.Guid) || {};
            const updatedTopic = {
                ...existingTopic,
                ...topic,
            };

            acc.set(topic.Guid, { ...updatedTopic, ...updateVisibility(updatedTopic), ...updateReadOnlyCriteria(updatedTopic) });
            return acc;
        }, new Map<string, StorytellingTopicRecord>(state.topics));
        return {
            ...state,
            topics: topicsMap,
        };
    }),
    on(TopicRecordActions.setTopicTemplates, (state, action): TopicsRecordState => {
        return {
            ...state,
            topicTemplates: new Map<string, StorytellingStoryRecord>(
                action.templates.map(r => {
                    return [r.Guid, { ...r, isDeleted: state.topicTemplates.get(r.Guid)?.isDeleted || false }];
                })
            ),
        };
    }),

    on(TopicRecordActions.updateTopicTemplate, (state, { templates }): TopicsRecordState => {
        if (!templates?.length) {
            return state;
        }
        const allTemplates = new Map<string, StorytellingTopicRecord>(state.topicTemplates);
        templates.forEach(template => {
            allTemplates.set(template?.Guid, template);
        });
        return {
            ...state,
            topicTemplates: allTemplates,
        };
    }),
    on(TopicRecordActions.deleteTopics, (state, { records }): TopicsRecordState => {
        if (!records?.length) {
            return state;
        }

        const topicsMap = new Map<string, StorytellingAssignmentRecord>(state.topics);
        records.forEach(topic => {
            topicsMap.delete(topic.Guid);
        });
        return {
            ...state,
            topics: topicsMap,
        };
    }),
    on(RecordHttpActions.deleteRecordTemplate, (state, { guid, recordType }): TopicsRecordState => {
        if (!guid?.length || recordType !== ObjectTypes.TOPIC) {
            return state;
        }

        const templatesMap = new Map<string, StorytellingTopicRecord>(state.topicTemplates);
        const template = templatesMap.get(guid);
        if (template) {
            const updatedTemplate = { ...template, isDeleted: true };
            templatesMap.set(guid, updatedTemplate);
        }
        return {
            ...state,
            topicTemplates: templatesMap,
        };
    })
);

export const rundownRecordsReducer = createReducer(
    initialRundownsState,
    on(RundownRecordActions.setRundowns, (state, action): RundownsRecordState => {
        const records = action.records.values().reduce((acc, rundown) => {
            const record = { ...rundown };
            const vis = updateVisibility(record);
            const readOnly = updateReadOnlyCriteria(record);
            rundown.isArchived = state.rundowns.get(record.Guid)?.isArchived || false;
            acc.set(record.Guid, { ...record, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingTopicRecord>());
        return {
            ...state,
            rundowns: records,
        };
    }),
    on(RundownRecordActions.updateRundowns, (state, { records, fromRTU }): RundownsRecordState => {
        if (!records?.length) {
            return state;
        }

        const rundownsMap = records.reduce((acc, rundown) => {
            const existingRundown = state.rundowns.get(rundown.Guid) || {};
            // This is needed because when we delete a story from RO in the client, the RTU contains only removedscripts, and not the updated sequence
            if (rundown.RemovedScripts?.length > 0 && existingRundown.Sequence?.length > 0) {
                existingRundown.Sequence = existingRundown.Sequence.filter(script => rundown.RemovedScripts.indexOf(script) === -1);
            }
            const updatedRundown = {
                ...existingRundown,
                ...rundown,
                fromRTU,
            };
            const vis = updateVisibility(updatedRundown);
            const readOnly = updateReadOnlyCriteria(updatedRundown);
            acc.set(rundown.Guid, { ...updatedRundown, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingRundownRecord>(state.rundowns));
        return {
            ...state,
            rundowns: rundownsMap,
        };
    }),
    on(RundownRecordActions.setRundownTemplates, (state, { templates }): RundownsRecordState => {
        return {
            ...state,
            rundownTemplates: templates.reduce((acc, template) => {
                acc.set(template.Guid, { ...template, isDeleted: state.rundownTemplates.get(template.Guid)?.isDeleted || false });
                return acc;
            }, new Map<string, StorytellingRundownRecord>()),
        };
    }),
    on(RundownRecordActions.updateRundownTemplates, (state, { templates }): RundownsRecordState => {
        if (!templates?.length) {
            return state;
        }
        const allTemplates = new Map<string, StorytellingRundownRecord>(state.rundownTemplates);
        templates.forEach(template => {
            const existingTemplate = allTemplates.get(template?.Guid) || {};
            allTemplates.set(template?.Guid, { ...existingTemplate, ...template });
        });
        return {
            ...state,
            rundownTemplates: allTemplates,
        };
    }),
    on(RundownRecordActions.deleteRundownTemplates, (state, { templates }): RundownsRecordState => {
        if (!templates?.length) {
            return state;
        }
        const templatesMap = new Map<string, StorytellingRundownRecord>(state.rundownTemplates);
        templates.forEach(template => {
            templatesMap.delete(template.Guid);
        });
        return {
            ...state,
            rundownTemplates: templatesMap,
        };
    }),
    on(RecordHttpActions.deleteRecord, (state, { record }): RundownsRecordState => {
        if (!record || record.Type !== ObjectTypes.RUNDOWN) {
            return state;
        }

        const rundownsMap = new Map<string, StorytellingRundownRecord>(state.rundowns);
        const rundown = rundownsMap.get(record.Guid);
        if (rundown) {
            const updatedRundown = { ...rundown, isDeleted: true };
            rundownsMap.set(record.Guid, updatedRundown);
        }
        return {
            ...state,
            rundowns: rundownsMap,
        };
    }),
    on(RecordHttpActions.deleteRecordTemplate, (state, { guid, recordType }): RundownsRecordState => {
        if (!guid?.length || recordType !== ObjectTypes.RUNDOWN) {
            return state;
        }

        const templatesMap = new Map<string, StorytellingRundownRecord>(state.rundownTemplates);
        const template = templatesMap.get(guid);
        if (template) {
            const updatedTemplate = { ...template, isDeleted: true };
            templatesMap.set(guid, updatedTemplate);
        }
        return {
            ...state,
            rundownTemplates: templatesMap,
        };
    }),
    on(RundownRecordActions.deleteRundowns, (state, { records }): RundownsRecordState => {
        if (!records?.length) {
            return state;
        }
        const rundownMap = new Map<string, StorytellingRundownRecord>(state.rundowns);
        records.forEach(rundown => {
            rundownMap.delete(rundown.Guid);
        });
        return {
            ...state,
            rundowns: rundownMap,
        };
    }),
    on(RundownRecordActions.archiveRundown, (state, { record, isCopy }): RundownsRecordState => {
        if (!isCopy) {
            const rundownMap = new Map<string, StorytellingRundownRecord>(state.rundowns);
            const existingRundown = rundownMap.get(record.Guid);
            if (existingRundown) {
                const updatedRundown = { ...existingRundown, isArchived: true };
                rundownMap.set(record.Guid, updatedRundown);
                return {
                    ...state,
                    rundowns: rundownMap,
                };
            }
        }

        return state;
    })
);

export const storiesRecordsReducer = createReducer(
    initialStoriesState,
    on(StoriesRecordActions.setStories, (state, action): StoriesRecordState => {
        const records = action.records.values().reduce((acc, record) => {
            const story = { ...record };
            story.FullStorySlug = story?.ContainerTitle?.length > 0 ? `${story?.Title || ''} [${story.ContainerTitle} ${toLuxonLocaleDateString(story.ContainerModTime)}]` : story?.Title || '';
            const vis = updateVisibility(story);
            const readOnly = updateReadOnlyCriteria(story);
            story.attachments = extractAttachments(story);
            acc.set(story.Guid, { ...story, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingStoryRecord>());
        return {
            ...state,
            stories: records,
        };
    }),
    on(StoriesRecordActions.updateStories, (state, { records, fromRTU }): StoriesRecordState => {
        if (!records?.length) {
            return state;
        }

        const storiesMap = records.reduce((acc, story) => {
            const existingStory = state.stories.get(story.Guid) || {};
            let updatedStory = {
                ...existingStory,
                ...story,
                fromRTU,
            };
            if (updatedStory.Title != null) {
                updatedStory.FullStorySlug =
                    updatedStory?.ContainerTitle?.length > 0
                        ? `${updatedStory?.Title || ''} [${updatedStory.ContainerTitle} ${toLuxonLocaleDateString(updatedStory.ContainerModTime)}]`
                        : updatedStory?.Title || '';
            } else {
                delete updatedStory.Title;
            }
            if (existingStory?.ModTime?.isValid && updatedStory.ModTime?.isValid && existingStory.ModTime > updatedStory.ModTime) {
                acc.set(existingStory.Guid, existingStory);
                return acc;
            }
            const vis = updateVisibility(updatedStory);
            const readOnly = updateReadOnlyCriteria(updatedStory);
            updatedStory = {
                ...existingStory,
                ...updatedStory,
                ...vis,
                ...readOnly,
            };
            updatedStory.attachments = extractAttachments(updatedStory);
            acc.set(updatedStory.Guid, updatedStory);
            return acc;
        }, new Map<string, StorytellingStoryRecord>(state.stories));

        return {
            ...state,
            stories: storiesMap,
        };
    }),
    on(StoriesRecordActions.setFlashState, (state, action): StoriesRecordState => {
        const rec = action.record;
        const existingRec = state.stories.get(rec.Guid) || null;
        const shouldFlashStory = (story: StorytellingStoryRecord, existingStory: StorytellingStoryRecord) => {
            return existingStory && existingStory?.Path?.isInCollection && (!isStoryTextEqual(existingStory.text, story.text) || !isStoryTextEqual(existingStory.Text, story.Text));
        };
        if (shouldFlashStory(rec, existingRec)) {
            return {
                ...state,
                stories: new Map(state.stories.set(rec.Guid, { ...existingRec, isRowFlashing: true })),
            };
        }
        return state;
    }),
    on(StoriesRecordActions.resetFlashState, (state, action): StoriesRecordState => {
        const rec = action.record;
        const existingRec = state.stories.get(rec.Guid) || null;
        if (existingRec) {
            return {
                ...state,
                stories: new Map(state.stories.set(rec.Guid, { ...existingRec, isRowFlashing: false })),
            };
        }
        return state;
    }),
    on(StoriesRecordActions.setStoryTemplates, (state, action): StoriesRecordState => {
        return {
            ...state,
            storyTemplates: new Map<string, StorytellingStoryRecord>(
                action.templates.map(r => {
                    return [r.Guid, { ...r, isDeleted: state.storyTemplates.get(r.Guid)?.isDeleted || false }];
                })
            ),
        };
    }),
    on(StoriesRecordActions.updateStoryTemplates, (state, { templates }): StoriesRecordState => {
        if (!templates?.length) {
            return state;
        }
        const allTemplates = new Map<string, StorytellingStoryRecord>(state.storyTemplates);
        templates.forEach(template => {
            allTemplates.set(template?.Guid, template);
        });
        return {
            ...state,
            storyTemplates: allTemplates,
        };
    }),
    on(StoriesRecordActions.deleteStoryTemplates, (state, { templates }): StoriesRecordState => {
        if (!templates?.some(template => state.storyTemplates.has(template?.Guid))) {
            return state;
        }
        const templatesMap = new Map<string, StorytellingStoryRecord>(state.storyTemplates);
        templates.forEach(template => {
            templatesMap.delete(template?.Guid);
        });
        return {
            ...state,
            storyTemplates: templatesMap,
        };
    }),
    on(StoriesRecordActions.deleteStories, (state, { records }): StoriesRecordState => {
        if (!records?.length) {
            return state;
        }

        const storiesMap = new Map<string, StorytellingStoryRecord>(state.stories);
        records.forEach(story => {
            storiesMap.delete(story.Guid);
        });

        return {
            ...state,
            stories: storiesMap,
        };
    }),
    on(RecordHttpActions.deleteRecordTemplate, (state, { guid, recordType }): StoriesRecordState => {
        if (!guid?.length || recordType !== ObjectTypes.SCRIPT) {
            return state;
        }

        const templatesMap = new Map<string, StorytellingStoryRecord>(state.storyTemplates);
        const template = templatesMap.get(guid);
        if (template) {
            const updatedTemplate = { ...template, isDeleted: true };
            templatesMap.set(guid, updatedTemplate);
        }
        return {
            ...state,
            storyTemplates: templatesMap,
        };
    }),
    on(RecordHttpActions.deleteRecord, (state, { record }): StoriesRecordState => {
        if (!record || record.Type !== ObjectTypes.SCRIPT) {
            return state;
        }

        const storiesMap = new Map<string, StorytellingStoryRecord>(state.stories);
        const story = storiesMap.get(record.Guid);
        if (story) {
            const updatedStory = { ...story, isDeleted: true };
            storiesMap.set(record.Guid, updatedStory);
        }
        return {
            ...state,
            stories: storiesMap,
        };
    }),
    on(LiveApiActions.addRundownStory, (state, { scriptGuid }): StoriesRecordState => {
        if (!scriptGuid) {
            return state;
        }
        const storiesMap = new Map<string, StorytellingStoryRecord>(state.stories);
        const story = storiesMap.get(scriptGuid);
        if (story) {
            const updatedStory = { ...story, isDisabled: true };
            storiesMap.set(scriptGuid, updatedStory);
            return {
                ...state,
                stories: storiesMap,
            };
        }
        return state;
    }),
    on(LiveApiActions.addRundownStorySuccess, LiveApiActions.addRundownStoryError, (state, { scriptGuid }): StoriesRecordState => {
        if (!scriptGuid) {
            return state;
        }
        const storiesMap = new Map<string, StorytellingStoryRecord>(state.stories);
        const story = storiesMap.get(scriptGuid);
        if (story) {
            const updatedStory = { ...story, isDisabled: false };
            storiesMap.set(scriptGuid, updatedStory);
            return {
                ...state,
                stories: storiesMap,
            };
        }
        return state;
    }),
    on(OpenFullscreenModalActions.openStoryFullscreenDialog, FullscreenActions.openStory, SidecarEfffectActions.openStoryInSidecar, (state, action): StoriesRecordState => {
        let guid = null;
        if (action.type === SidecarEfffectActions.openStoryInSidecar.type) {
            guid = action.guid;
        } else {
            guid = action.recordGuid;
        }
        const story = state.stories.get(guid);
        if (story) {
            return {
                ...state,
                stories: new Map(state.stories).set(guid, { ...story, loadedBasicContent: false }),
            };
        }
        return state;
    })
);

export const assignmentRecordsReducer = createReducer(
    initialAssignmentsState,
    on(AssignmentRecordActions.setAssignments, (state, action): AssignmentsRecordState => {
        const records = action.records.values().reduce((acc, record) => {
            const assignment = { ...record };
            const vis = updateVisibility(assignment);
            const readOnly = updateReadOnlyCriteria(assignment);
            assignment.attachments = extractAttachments(assignment);
            acc.set(assignment.Guid, { ...assignment, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingStoryRecord>());
        return {
            ...state,
            assignments: records,
        };
    }),
    on(AssignmentRecordActions.updateAssignments, (state, { records }): AssignmentsRecordState => {
        if (!records?.length) {
            return state;
        }

        const assignmentsMap = records.reduce((acc, assignment) => {
            const existingAssignment = state.assignments.get(assignment.Guid) || {};
            const updatedAssignment = {
                ...existingAssignment,
                ...assignment,
            };
            updatedAssignment.attachments = extractAttachments(updatedAssignment);
            const vis = updateVisibility(updatedAssignment);
            const readOnly = updateReadOnlyCriteria(updatedAssignment);
            acc.set(assignment.Guid, { ...updatedAssignment, ...vis, ...readOnly });
            return acc;
        }, new Map<string, StorytellingAssignmentRecord>(state.assignments));

        return {
            ...state,
            assignments: assignmentsMap,
        };
    }),
    on(AssignmentRecordActions.deleteAssignments, (state, { records }): AssignmentsRecordState => {
        if (!records?.length) {
            return state;
        }

        const assignmentsMap = new Map<string, StorytellingAssignmentRecord>(state.assignments);
        records.forEach(story => {
            assignmentsMap.delete(story.Guid);
        });

        return {
            ...state,
            assignments: assignmentsMap,
        };
    })
);

export const userRecordsReducer = createReducer(
    initialStorytellingUsersState,
    on(UserRecordActions.setUsers, (state, action): UsersRecordState => {
        return {
            ...state,
            users: action.records,
        };
    }),
    on(UserRecordActions.updateUsers, (state, { records }): UsersRecordState => {
        if (!records?.length) {
            return state;
        }

        const usersMap = records.reduce((acc, user) => {
            const existingUser = state.users.get(user.Guid) || {};
            const updatedUser = {
                ...existingUser,
                ...user,
            };
            // updateVisibility(updatedUser);
            // updateReadOnlyCriteria(updatedUser);
            acc.set(user.Guid, updatedUser);
            return acc;
        }, new Map<string, StorytellingUserRecord>(state.users));

        return {
            ...state,
            users: usersMap,
        };
    })
);

export const discoverRecordsReducer = createReducer(
    initialDiscoverRecordState,
    on(DiscoverRecordActions.setDiscoverItems, (state, action): DiscoverRecordState => {
        return {
            ...state,
            discoverItems: action.records,
        };
    })
);

export const publicationRecordsReducer = createReducer(
    initialPublicationsState,
    on(PublicationRecordActions.setPublications, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publications: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublications, (state, { records }): PublicationsRecordState => {
        if (!records?.length) {
            return state;
        }

        const publicationsMap = records.reduce((acc, publication) => {
            const existingPublication = state.publications.get(publication.Guid) || {};
            const updatedPublication = {
                ...existingPublication,
                ...publication,
            };
            acc.set(publication.Guid, updatedPublication);
            return acc;
        }, new Map<string, StorytellingPublicationRecord>(state.publications));

        return {
            ...state,
            publications: publicationsMap,
        };
    }),
    on(PublicationRecordActions.setPublicationChannels, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publicationChannels: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublicationChannels, (state, action): PublicationsRecordState => {
        if (!action.records?.length) {
            return state;
        }

        const publicationChannelsMap = action.records.reduce((acc, channel) => {
            const existingChannel = state.publicationChannels.get(channel.Guid) || {};
            const updatedChannel = {
                ...existingChannel,
                ...channel,
            };
            acc.set(channel.Guid, updatedChannel);
            return acc;
        }, new Map<string, StorytellingPublicationChannelRecord>(state.publicationChannels));

        return {
            ...state,
            publicationChannels: publicationChannelsMap,
        };
    }),
    on(PublicationRecordActions.setPublicationList, (state, action): PublicationsRecordState => {
        return {
            ...state,
            publicationList: action.records,
        };
    }),
    on(PublicationRecordActions.updatePublicationList, (state, action): PublicationsRecordState => {
        if (!action.records?.length) {
            return state;
        }

        const publicationListMap = action.records.reduce((acc, list) => {
            const existingList = state.publicationList.get(list.Guid) || {};
            const updatedList = {
                ...existingList,
                ...list,
            };
            acc.set(list.Guid, updatedList);
            return acc;
        }, new Map<string, StorytellingPublicationListRecord>(state.publicationList));

        return {
            ...state,
            publicationList: publicationListMap,
        };
    })
);

export const notesRecordsReducer = createReducer(
    initialNotesState,
    on(NotesRecordActions.setNotes, (state, action): NotesRecordState => {
        const newExpandState: Record<string, boolean> = {};
        const records = action.records.values().reduce((acc, record) => {
            const note = { ...record };
            const vis = updateVisibility(note);
            const readOnly = updateReadOnlyCriteria(note);
            acc.set(note.Guid, { ...note, ...vis, ...readOnly });
            newExpandState[note.Guid] = state.notesExpandState[note.Guid] ?? true;
            return acc;
        }, new Map<string, StorytellingNoteRecord>());
        return {
            ...state,
            notes: records,
            notesExpandState: newExpandState,
        };
    }),
    on(NotesRecordActions.changeNoteExpandedState, (state, { guid, isExpanded }): NotesRecordState => {
        return {
            ...state,
            notesExpandState: {
                ...state.notesExpandState,
                [guid]: isExpanded,
            },
        };
    }),
    on(NotesRecordActions.changeAllNotesExpandedState, (state, { isExpanded }): NotesRecordState => {
        const newExpandState = Object.keys(state.notesExpandState).reduce((acc, guid) => {
            acc[guid] = isExpanded;
            return acc;
        }, {});
        return {
            ...state,
            notesExpandState: newExpandState,
        };
    }),
    on(NotesRecordActions.updateNotes, (state, { records }): NotesRecordState => {
        if (!records?.length) {
            return state;
        }

        const notesExpandState = { ...state.notesExpandState };
        const notesMap = records.reduce((acc, value) => {
            const note = { ...value };
            const existingNote = state.notes.get(note.Guid) || {};
            Object.keys(existingNote)
                .filter(key => existingNote[key]?.timeZone)
                .forEach(key => {
                    if (existingNote[key]?.timeZone) {
                        note[key].timeZone = existingNote[key].timeZone;
                    }
                });
            const updatedNote = {
                ...existingNote,
                ...note,
            };
            const vis = updateVisibility(updatedNote);
            const readOnly = updateReadOnlyCriteria(updatedNote);
            acc.set(note.Guid, { ...updatedNote, ...vis, ...readOnly });
            if (!(note.Guid in notesExpandState)) {
                notesExpandState[note.Guid] = true;
            }
            return acc;
        }, new Map<string, StorytellingNoteRecord>(state.notes));

        return {
            ...state,
            notes: notesMap,
            notesExpandState: notesExpandState,
        };
    }),
    on(NotesRecordActions.deleteNotes, (state, { records }): NotesRecordState => {
        if (!records?.length) {
            return state;
        }
        const notesMap = new Map<string, StorytellingNoteRecord>(state.notes);
        const notesExpandState = { ...state.notesExpandState };
        records.forEach(note => {
            notesMap.delete(note.Guid);
            delete notesExpandState[note.Guid];
        });
        return {
            ...state,
            notes: notesMap,
            notesExpandState,
        };
    }),
    on(NotesRecordActions.setNoteTemplates, (state, action): NotesRecordState => {
        return {
            ...state,
            noteTemplates: new Map<string, StorytellingNoteRecord>(
                action.templates.map(r => {
                    return [
                        r.Guid,
                        {
                            ...r,
                            noteBeginText: r.Guid === OPTION_BLANK ? r.noteBeginText : extractNoteBeginText(r.RawContent),
                            isDeleted: state.noteTemplates.get(r.Guid)?.isDeleted || false,
                        } as StorytellingNoteRecord,
                    ];
                })
            ),
        };
    }),
    on(NotesRecordActions.updateNoteTemplates, (state, { templates }): NotesRecordState => {
        if (!templates?.length) {
            return state;
        }
        const allTemplates = new Map<string, StorytellingNoteRecord>(state.noteTemplates);
        templates.forEach(template => {
            allTemplates.set(template?.Guid, { ...template, noteBeginText: template.Guid === OPTION_BLANK ? template.noteBeginText : extractNoteBeginText(template.RawContent) });
        });
        return {
            ...state,
            noteTemplates: allTemplates,
        };
    }),
    on(RecordHttpActions.deleteRecordTemplate, (state, { guid, recordType }): NotesRecordState => {
        if (!guid?.length || recordType !== ObjectTypes.NOTE) {
            return state;
        }

        const templatesMap = new Map<string, StorytellingNoteRecord>(state.noteTemplates);
        const template = templatesMap.get(guid);
        if (template) {
            const updatedTemplate = { ...template, isDeleted: true };
            templatesMap.set(guid, updatedTemplate);
        }
        return {
            ...state,
            noteTemplates: templatesMap,
        };
    })
);
